import classNames from 'classnames';
import React, { useMemo } from 'react';

import { ClassNameMap, makeStyles } from '@mui/styles';

import {
  MemberEngagementLevel,
  ProgramTaskReviewStatus,
  ProgramTaskStatus,
} from '@vestahealthcare/common/enums';
import { translate } from '@vestahealthcare/common/i18n';
import { PaginationType, ProgramTask } from '@vestahealthcare/common/models';
import {
  DATE_FORMAT_SHORT,
  EMPTY,
} from '@vestahealthcare/common/utils/constants';

import {
  BrandMemberIcon,
  CheckboxWithLabel,
  Chip,
  CopyIcon,
  Select,
  StatusIndicator,
  Table,
  Tooltip,
  UpdatedBy,
} from 'styleguide-v2';
import { Column, TableItem } from 'styleguide-v2/src/components/Table';
import { ColorOption } from 'styleguide-v2/src/utils/colors';

import { HouseHoldMembersChip } from 'dash/src/components/HouseHoldMembersChip';

import { EngagementIcon } from '../../../components/Icons/EngagementIcon';

type TaskTableItem = ProgramTask & TableItem;

interface Props {
  allChecked: boolean;
  blacklist: boolean;
  defaultPageSize?: number;
  diabledAdminStatus?: boolean;
  disabledChecks?: boolean;
  disabledChecksLabel?: string;
  defaultSort?: string;
  items: TaskTableItem[];
  pagination?: PaginationType;
  onChangePage: (page: number) => void;
  onChangePageSize: (pageSize: number) => void;
  onChangeReviewStatus: (
    task: ProgramTask,
    newStatus: ProgramTaskReviewStatus,
  ) => Promise<void>;
  onSelectTask: (task: ProgramTask) => void;
  onSelectAll: (allChecked: boolean) => void;
  selectedIds: number[];
  showChecks?: boolean;
}

const TABLE_HEADERS = {
  assignee: translate('programTasksManagement.table.assignee'),
  member: translate('programTasksManagement.table.member'),
  pod: translate('programTasksManagement.table.pod'),
  status: translate('programTasksManagement.table.status'),
  taskDefinition: translate('programTasksManagement.table.taskDefinition'),
};

const getReviewColor = (
  statusReview?: ProgramTaskReviewStatus,
): ColorOption | undefined => {
  if (
    [
      ProgramTaskReviewStatus.NOT_REVIEWED,
      ProgramTaskReviewStatus.REVIEWED,
      undefined,
    ].includes(statusReview)
  )
    return 'secondary';
  if (ProgramTaskReviewStatus.IN_PROGRESS === statusReview) return 'info';
  if (ProgramTaskReviewStatus.ESCALATED_ACCOUNTS === statusReview)
    return 'error';
};

const FORECAST_TASK_COLUMNS = (
  styles: ClassNameMap<'small' | 'extraSmall' | 'flexItem'>,
  {
    allChecked,
    blacklist,
    diabledAdminStatus,
    disabledChecks,
    disabledChecksLabel,
    items,
    onSelectAll,
    onSelectTask,
    selectedIds,
    showChecks,
  }: Props,
  onChangeReviewStatus: (
    task: ProgramTask,
    newStatus: ProgramTaskReviewStatus,
  ) => Promise<void>,
) => {
  const colums: Column[] = [
    {
      headerName: TABLE_HEADERS.status,
      component: (task: TaskTableItem) =>
        task.status ? (
          <div className="flex gap spaced">
            <StatusIndicator
              className={styles.flexItem}
              status={task.status.toString()}
              color={ProgramTaskStatus.getColor(task.status)}
              subtitle={
                diabledAdminStatus &&
                task.shouldShowReviewStatus &&
                (
                  task.adminStatus || ProgramTaskReviewStatus.NOT_REVIEWED
                )?.toString()
              }
            />
            {!diabledAdminStatus && task.shouldShowReviewStatus && (
              <Select
                className={styles.flexItem}
                color={getReviewColor(task.adminStatus)}
                disableClearable
                getItemLabel={(status: ProgramTaskReviewStatus) =>
                  status.toString()
                }
                items={ProgramTaskReviewStatus.asArray}
                loading={task.loading}
                key={`task-${task.id}-review-status`}
                onChange={(newVal: ProgramTaskReviewStatus) =>
                  onChangeReviewStatus(task, newVal)
                }
                type={
                  [ProgramTaskReviewStatus.NOT_REVIEWED, undefined].includes(
                    task.adminStatus,
                  )
                    ? 'contained'
                    : 'outlined'
                }
                size="xs"
                value={task.adminStatus || ProgramTaskReviewStatus.NOT_REVIEWED}
              />
            )}
          </div>
        ) : (
          EMPTY
        ),
      width: 400,
    },
    {
      headerName: TABLE_HEADERS.taskDefinition,
      component: ({ taskDate, name }: ProgramTask) =>
        taskDate ? (
          <UpdatedBy
            user={name}
            date={taskDate}
            dateFormat={DATE_FORMAT_SHORT}
          />
        ) : (
          name
        ),
    },
    {
      headerName: TABLE_HEADERS.pod,
      field: 'member.pod.name',
    },
    {
      headerName: TABLE_HEADERS.member,
      component: ({ member }: ProgramTask) => (
        <>
          <p
            className={classNames(
              'bold no-margin flex middle wrap',
              styles.small,
            )}
          >
            {member.fullName}
            &nbsp;
            <HouseHoldMembersChip householdMembers={member?.householdMembers} />
            {member.isBrandCareAtHome() && (
              <BrandMemberIcon brand="careAtHome" size="s" />
            )}
            {member.engagementLevel && (
              <Tooltip
                text={translate(
                  'programTasksManagement.table.engagementLevel',
                  {
                    level: member.engagementLevel.toString(),
                  },
                )}
              >
                <div className="flex">
                  <EngagementIcon
                    color={MemberEngagementLevel.getColor(
                      member.engagementLevel,
                    )}
                    size="small"
                  />
                </div>
              </Tooltip>
            )}
          </p>
          (
          <a
            className={styles.small}
            href={`#/patients/${member?.id}`}
            rel="noreferrer"
            target="_blank"
            onClick={(evt) => evt.stopPropagation()}
          >
            {member?.id}
          </a>{' '}
          <CopyIcon
            className="no-padding"
            copyLabel={translate('components.MemberInfo.copyId')}
            value={member.id.toString()}
            size="small"
          />
          )
          {member?.language && (
            <>
              {' '}
              <Chip color="info" size="small" type="outlined">
                {member.language?.toString()}
              </Chip>
            </>
          )}
          {member?.state && (
            <span className={styles.small}> {member.state?.value}</span>
          )}
        </>
      ),
      width: 200,
    },
    {
      headerName: TABLE_HEADERS.assignee,
      field: 'assignee.fullName',
    },
  ];

  const headerCheck = (
    <CheckboxWithLabel
      checked={allChecked}
      disabled={!items.length || disabledChecks}
      onChange={() => onSelectAll(allChecked)}
    />
  );
  const rowCheck = (task: ProgramTask) => (
    <CheckboxWithLabel
      disabled={disabledChecks}
      checked={blacklist !== selectedIds.includes(task.id)}
      onChange={() => onSelectTask(task)}
    />
  );

  return showChecks
    ? [
        {
          headerName: disabledChecks ? (
            <Tooltip text={disabledChecksLabel || 'Disabled'}>
              <div>{headerCheck}</div>
            </Tooltip>
          ) : (
            headerCheck
          ),
          component: (task: ProgramTask) =>
            disabledChecks ? (
              <Tooltip text={disabledChecksLabel || 'Disabled'}>
                <div>{rowCheck(task)}</div>
              </Tooltip>
            ) : (
              rowCheck(task)
            ),
          width: 50,
        },
        ...colums,
      ]
    : colums;
};

const useStyles = makeStyles({
  extraSmall: { fontSize: '0.75em', lineHeight: '16px' },
  flexItem: { flex: 1 },
  small: { fontSize: '0.875em', lineHeight: '16px' },
});

export const ProgramTaskManagementTable = (props: Props) => {
  const styles = useStyles();

  const onChangeReviewStatus = async (
    item: ProgramTask,
    newStatus: ProgramTaskReviewStatus,
  ) => {
    await props.onChangeReviewStatus(item, newStatus);
  };

  const columns = useMemo(
    () => FORECAST_TASK_COLUMNS(styles, props, onChangeReviewStatus),
    [props.onChangeReviewStatus, props.onSelectTask, styles],
  );

  return (
    <Table
      config={{
        columns,
        compact: true,
        data: props.items,
        defaultPageSize: props.defaultPageSize,
        getRowBrand: ({ member }: ProgramTask) => {
          if (member?.isVPC() || member?.isVPCLite()) return 'VPC';
          return member?.brand;
        },
        pagination: props.pagination,
        paginationOptions: [25, 50, 100],
      }}
      empty
      fixedHeader
      onChangePage={props.onChangePage}
      onChangeRowsPerPage={props.onChangePageSize}
    />
  );
};

export default ProgramTaskManagementTable;
